'use client';

import { useMemo, useState } from 'react';
import { Box, VStack } from '@terra/ui-styled-system';
import { Edit03Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import type {
  MentionEntity,
  MessageSegment,
} from '@terra/shared-dto-chat-types';
import { IconButton } from '../../../../../components/icon-button';
import { Text } from '../../../../../components/styled/text';
import { Tooltip } from '../../../../../components/tooltip';
import { InlineMessageContent } from '../../InlineMessageContent';

export {
  readAskUserAnswer,
  readAskUserPrompt,
  readAskUserOptions,
} from './ask-user-answer-card-view.utils';
export function AskUserAnswerCardView({
  prompt,
  answer,
  skipped,
  answerMentions,
  answerSegments,
  onChangeAnswer,
}: {
  prompt: string;
  answer: string;
  skipped: boolean;
  answerMentions?: MentionEntity[];
  answerSegments?: MessageSegment[];
  onChangeAnswer?: () => void;
}) {
  const tBranching = useTranslations('designSystem.chat.branching');
  const t = useTranslations('designSystem.chat.askUserPromptCard');
  const [isHovered, setIsHovered] = useState(false);
  const answerText = skipped ? t('skipped') : answer;
  const mentionEntitiesById = useMemo(() => {
    if (!answerMentions?.length) {
      return undefined;
    }

    return new Map(
      answerMentions.map((mention) => [mention.entityId, mention]),
    );
  }, [answerMentions]);
  const showMentionAnswer =
    !skipped && !!answerSegments?.length && !!mentionEntitiesById?.size;

  if (!answerText && !showMentionAnswer) {
    return null;
  }

  return (
    <Box
      position="relative"
      bg="neutral.a4"
      borderRadius="md"
      overflow="hidden"
      width="100%"
      paddingInline="10px"
      paddingBlock="6px"
      onMouseEnter={onChangeAnswer ? () => setIsHovered(true) : undefined}
      onMouseLeave={onChangeAnswer ? () => setIsHovered(false) : undefined}
    >
      <VStack alignItems="start" gap={1} width="100%">
        {prompt ? (
          <Text
            fontSize="xs"
            color="fg.muted"
            lineHeight="1.5"
            whiteSpace="pre-wrap"
            wordBreak="break-word"
          >
            {prompt}
          </Text>
        ) : null}
        {showMentionAnswer && answerSegments ? (
          <InlineMessageContent
            segments={answerSegments}
            mentionEntitiesById={mentionEntitiesById}
          />
        ) : (
          <Text
            fontSize="sm"
            fontWeight="medium"
            color="fg.muted"
            lineHeight="1.5"
            whiteSpace="pre-wrap"
            wordBreak="break-word"
          >
            {answerText}
          </Text>
        )}
      </VStack>

      {onChangeAnswer ? (
        <Box
          position="absolute"
          top="50%"
          right={1}
          transform="translateY(-50%)"
          opacity={isHovered ? 1 : 0}
          pointerEvents={isHovered ? 'auto' : 'none'}
          transition="opacity 0.15s ease"
        >
          <Tooltip title={tBranching('changeAnswer')}>
            <IconButton
              aria-label={tBranching('changeAnswer')}
              variant="ghost"
              size="xs"
              color="fg.muted"
              onClick={onChangeAnswer}
            >
              <Edit03Icon size={14} />
            </IconButton>
          </Tooltip>
        </Box>
      ) : null}
    </Box>
  );
}
